import React, { useState, useEffect } from 'react';
import P, { InferProps } from 'prop-types';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { SxStyle } from '../../../types';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { Avatar, ListItemAvatar, ListItemIcon, ListItemText } from '@mui/material';
import firebaseApp from '../../../firebase-app';
import { useStringsContext } from '../../../contexts/strings';
import { Promo } from '../../../types/app-config';
import { getAppConfigPath } from '../../../utils/paths';
import { PROMO_VARIANT } from '../../../types/team';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { doc } from 'firebase/firestore';
import AlarmIcon from '@mui/icons-material/Alarm';

const styles: SxStyle = {
  formControl: {
    minWidth: 214,
  },
  timedIcon: {
    paddingLeft: '4px',
  },
};

function PromoSelectField({
  field,
  setChange,
  isReadOnly,
  path,
}: InferProps<typeof PromoSelectField.propTypes>) {
  const [value, setValue] = useState<string | number>(field.value || '');
  const [open, setOpen] = useState<boolean>(false);
  const { strings } = useStringsContext();

  const [appConfig, loading, error] = useDocumentData(doc(firebaseApp.db, getAppConfigPath(path)));

  const promos: Promo[] = appConfig?.promos;

  useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e: SelectChangeEvent<string | number>) => {
    const inputValue = e.target.value;
    if (typeof inputValue === 'string' || typeof inputValue === 'number') {
      if (setChange) {
        setChange(field.key, inputValue, field.validation);
      }
    }
  };

  if (loading || error) {
    return (
      <FormControl variant="outlined" sx={styles.formControl}>
        <InputLabel id={`${field.id}-label`} htmlFor={field.id}>
          {field.title}
        </InputLabel>
        <Select
          id={field.id}
          labelId={`${field.id}-label`}
          disabled={!!isReadOnly}
          value=""
          label={field.title}
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
        >
          <MenuItem value="">
            <em>{error ? strings.messages.error : strings.messages.loading}</em>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <>
      <FormControl variant="outlined" sx={styles.formControl}>
        <InputLabel id={`${field.id}-label`} htmlFor={field.id}>
          {field.title}
        </InputLabel>
        <Select
          disabled={!!isReadOnly}
          labelId={`${field.id}-label`}
          id={field.id}
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          value={value}
          onChange={handleChange}
          renderValue={(value) => `Promo ${value}`}
          color="primary"
          label={field.title}
        >
          <MenuItem value="">
            <ListItemIcon>
              <ListItemAvatar>
                <Avatar variant="square">
                  <DeleteIcon />
                </Avatar>
              </ListItemAvatar>
            </ListItemIcon>
            <ListItemText primary={strings.messages.none} />
          </MenuItem>
          {field.options
            .filter((option: string | number | null) =>
              field.optionsFilter
                ? promos[option as number]?.promo_type === field.optionsFilter
                : true
            )
            .map((option: string | number | null) => {
              const promo = promos[option as number];
              const promoText = `Promo ${option as string}`;
              return (
                <MenuItem value={option as string} key={`${field.id}-${option}`}>
                  <ListItemIcon>
                    <ListItemAvatar>
                      {promo?.promo_type === PROMO_VARIANT.FORZASYS_VIDEO ? (
                        <Avatar variant="square">
                          <PlayCircleOutlineIcon />
                        </Avatar>
                      ) : (
                        <Avatar alt={promoText} src={promo?.contentUrl || ''} variant="square" />
                      )}
                    </ListItemAvatar>
                  </ListItemIcon>
                  {promo ? (
                    <>
                      <ListItemText primary={promoText} />
                      {promo.timed && <AlarmIcon sx={styles.timedIcon} />}
                    </>
                  ) : (
                    <ListItemText primary={`${promoText} (${strings.promos.promoEmpty})`} />
                  )}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    </>
  );
}

PromoSelectField.propTypes = {
  setChange: P.func,
  isReadOnly: P.bool,
  path: P.string.isRequired,
  field: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string,
    validation: P.any,
    key: P.string,
    optionsFilter: P.string,
    options: P.arrayOf(P.oneOfType([P.string, P.number]).isRequired).isRequired,
    value: P.oneOfType([P.string, P.number]).isRequired,
  }).isRequired,
};

export default React.memo(PromoSelectField);
